/* eslint-disable react/prop-types */
import React, { useState, useRef, useMemo, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Chip,
  TextField,
  Alert,
  Stack,
  MenuItem,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  CircularProgress,
  Tooltip,
  Popover,
  Divider,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useDispatch, useSelector } from "react-redux";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { theme } from "../../Theme";
import { PROMO_COLORS } from "./constants/colors";
import { displayOptimizerWarning } from "./utils/promoWarningLabels";
import { formatPromoTypeDisplay } from "./utils/promoDiscountBands";
import {
  setOptimizerResults,
  setOptimizerTargetVolume,
  setOptimizerTargetContributionMargin,
  setOptimizerLastTargets,
  setSelectedBrand,
  setSelectedManufacturer,
  setSelectedClass,
  setOptimizerWeekStart,
  setOptimizerWeekEnd,
  removeFromBatchOptimizerQueue,
  clearBatchOptimizerQueue,
} from "../../Redux/promoSimulatorSlice/promoSimulatorSlice";
import {
  promoSimulatorService,
  PROMO_ALL_BRANDS_OPTION,
  PROMO_ALL_BRANDS_ID,
  PROMO_ALL_CLASSES_ID,
  PROMO_DEFAULT_MANUFACTURER,
} from "./services/promoSimulatorService";
import { usePromoCatalog } from "./hooks/usePromoCatalog";

// ─── Warning severity helpers ─────────────────────────────────────────────────

function getWarningSeverityConfig(severity) {
  switch (String(severity || "").toLowerCase()) {
    case "critical":
      return { color: "#B91C1C", bg: "#FEF2F2", border: "#FCA5A5", label: "Critical" };
    case "high":
      return { color: "#C2410C", bg: "#FFF7ED", border: "#FDBA74", label: "High" };
    case "medium":
      return { color: "#B45309", bg: "#FFFBEB", border: "#FDE68A", label: "Medium" };
    case "info":
    default:
      return { color: "#1D4ED8", bg: "#EFF6FF", border: "#BFDBFE", label: "Info" };
  }
}

function severityOrder(severity) {
  const map = { critical: 0, high: 1, medium: 2, info: 3 };
  return map[String(severity || "").toLowerCase()] ?? 3;
}

// ─── Warning grouping ─────────────────────────────────────────────────────────

const TARGET_WARNING_CODES = new Set([
  "CM_TARGET_NOT_ACHIEVED", "VOLUME_TARGET_NOT_ACHIEVED",
  "TARGET_CM_NOT_ACHIEVED", "TARGET_CM_COULD_NOT_BE_ACHIEVED",
  "TARGET_NOT_ACHIEVED", "TARGET_VOLUME_NOT_ACHIEVED",
]);
const BUSINESS_RISK_CODES = new Set([
  "LOW_ROI", "CANNIBALIZATION_RISK", "HIGH_CANNIBALIZATION",
  "MUST_PROMOTE", "MUST_PROMOTE_WEEKS", "NEGATIVE_MARGIN", "LOW_MARGIN",
  "POOR_ROI", "BELOW_THRESHOLD", "MARGIN_AT_RISK",
]);
const DATA_QUALITY_CODES = new Set([
  "MISSING_DATA", "NO_HISTORICAL_DATA", "DATA_INSUFFICIENT",
  "INSUFFICIENT_DATA", "LOW_CONFIDENCE", "DATA_GAP",
]);

function groupWarnings(warnings) {
  const target = [], businessRisk = [], dataQuality = [];
  for (const w of warnings) {
    const code = String(w.code || "").toUpperCase();
    if (TARGET_WARNING_CODES.has(code) || w.scope === "plan") {
      target.push(w);
    } else if (DATA_QUALITY_CODES.has(code)) {
      dataQuality.push(w);
    } else if (BUSINESS_RISK_CODES.has(code) || w.scope === "sku_week") {
      businessRisk.push(w);
    } else {
      businessRisk.push(w);
    }
  }
  return { target, businessRisk, dataQuality };
}

// ─── Normalize warnings from API response ────────────────────────────────────

function normalizeWarningItem(item) {
  if (item == null) return null;
  if (typeof item === "string") {
    const s = item.trim();
    if (!s) return null;
    return { code: s, severity: "medium", message: displayOptimizerWarning(s), scope: "plan" };
  }
  if (typeof item === "object") {
    const msg = item.message != null ? String(item.message).trim() : null;
    if (!msg && !item.code) return null;
    return {
      code: item.code ?? null,
      severity: item.severity ?? "medium",
      message: msg || displayOptimizerWarning(item.code) || item.code,
      scope: item.scope ?? null,
      metric_value: item.metric_value ?? null,
      threshold: item.threshold ?? null,
      sku_name: item.sku_name ?? null,
      week: item.week ?? null,
    };
  }
  return null;
}

function normalizeOptimizerWarnings(payload) {
  const w = payload?.warnings;
  if (w == null) return [];
  if (Array.isArray(w)) {
    return w.map(normalizeWarningItem).filter(Boolean).sort((a, b) => severityOrder(a.severity) - severityOrder(b.severity));
  }
  if (typeof w === "string" && w.trim()) {
    const item = normalizeWarningItem(w);
    return item ? [item] : [];
  }
  return [];
}

// ─── WarningBadge: clickable badge that opens a detail popover ───────────────

function WarningBadge({ warning, skuName, week, compact = false }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const cfg = getWarningSeverityConfig(warning.severity);
  const open = Boolean(anchorEl);

  const displayLabel = displayOptimizerWarning(warning.code || warning.message) || warning.message || warning.code || "Warning";
  const resolvedSku = skuName || warning.sku_name || null;
  const resolvedWeek = week ?? warning.week ?? null;
  const SeverityIcon = warning.severity === "critical" || warning.severity === "high" || warning.severity === "medium"
    ? WarningAmberIcon
    : InfoOutlinedIcon;

  return (
    <>
      <Box
        role="button"
        tabIndex={0}
        aria-label={`Warning: ${displayLabel} — click for details`}
        onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); }}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); setAnchorEl(e.currentTarget); } }}
        sx={{
          display: "inline-flex",
          alignItems: "flex-start",
          gap: 0.55,
          px: compact ? 0.7 : 1,
          py: compact ? 0.3 : 0.5,
          borderRadius: 1,
          backgroundColor: cfg.bg,
          border: `1px solid ${cfg.border}`,
          cursor: "pointer",
          maxWidth: "100%",
          transition: "opacity 0.15s",
          "&:hover": { opacity: 0.8 },
          "&:focus-visible": { outline: `2px solid ${cfg.color}`, outlineOffset: 1 },
        }}
      >
        <SeverityIcon sx={{ fontSize: compact ? 13 : 15, color: cfg.color, flexShrink: 0, mt: "1px" }} />
        <Typography
          variant="caption"
          sx={{
            fontSize: compact ? 11 : 13,
            fontWeight: 600,
            color: cfg.color,
            lineHeight: 1.35,
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
        >
          {displayLabel}
        </Typography>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          elevation: 6,
          sx: { borderRadius: 2, maxWidth: 340, width: "100%", border: `1.5px solid ${cfg.border}`, overflow: "hidden" },
        }}
      >
        {/* Popover header */}
        <Box sx={{ px: 2, py: 1.5, backgroundColor: cfg.bg, borderBottom: `1px solid ${cfg.border}` }}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <SeverityIcon sx={{ fontSize: 20, color: cfg.color, mt: 0.2, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                <Chip
                  label={cfg.label}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: 12,
                    fontWeight: 700,
                    backgroundColor: `${cfg.color}20`,
                    color: cfg.color,
                    border: `1px solid ${cfg.border}`,
                    "& .MuiChip-label": { px: 0.85 },
                  }}
                />
              </Stack>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{ fontSize: 14, color: "text.primary", mt: 0.5, lineHeight: 1.4, wordBreak: "break-word" }}
              >
                {warning.message || displayLabel}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Popover body */}
        <Box sx={{ px: 2, py: 1.25, backgroundColor: "#fff" }}>
          {resolvedSku && (
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ py: 0.5, gap: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13, flexShrink: 0 }}>SKU</Typography>
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13, color: "text.primary", textAlign: "right", wordBreak: "break-word" }}>{resolvedSku}</Typography>
            </Stack>
          )}
          {resolvedWeek != null && (
            <>
              <Divider sx={{ borderColor: "#F1F5F9" }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>Week</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13, color: "text.primary" }}>Week {resolvedWeek}</Typography>
              </Stack>
            </>
          )}
          {warning.metric_value != null && (
            <>
              <Divider sx={{ borderColor: "#F1F5F9" }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>Metric value</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13, color: "text.primary" }}>{String(warning.metric_value)}</Typography>
              </Stack>
            </>
          )}
          {warning.threshold != null && (
            <>
              <Divider sx={{ borderColor: "#F1F5F9" }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>Threshold</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13, color: "text.primary" }}>{String(warning.threshold)}</Typography>
              </Stack>
            </>
          )}
        </Box>
      </Popover>
    </>
  );
}

function rowWarningsFromApi(row) {
  const m = row?.metrics ?? {};
  const raw = Array.isArray(m.warnings) ? m.warnings : Array.isArray(row?.warnings) ? row.warnings : null;
  if (raw === null) return null;
  return raw.map(normalizeWarningItem).filter(Boolean).sort((a, b) => severityOrder(a.severity) - severityOrder(b.severity));
}

const MAX_INLINE_WARNINGS = 2;

function RowWarningsCell({ row }) {
  const [expanded, setExpanded] = useState(false);
  const warnings = rowWarningsFromApi(row);
  const skuName = row?.sku_name ?? null;
  const week = row?.week ?? null;

  if (warnings === null) {
    return (
      <Typography variant="caption" color="text.disabled">—</Typography>
    );
  }
  if (warnings.length === 0) {
    return (
      <Stack direction="row" alignItems="center" spacing={0.6}>
        <CheckCircleOutlineIcon sx={{ fontSize: 14, color: PROMO_COLORS.safe }} />
        <Typography variant="caption" sx={{ color: PROMO_COLORS.safe, fontWeight: 600, fontSize: 11 }}>
          No warnings
        </Typography>
      </Stack>
    );
  }

  const visible = expanded ? warnings : warnings.slice(0, MAX_INLINE_WARNINGS);
  const hiddenCount = warnings.length - MAX_INLINE_WARNINGS;

  return (
    <Stack spacing={0.5} sx={{ minWidth: 0, py: 0.25 }}>
      {visible.map((w, i) => (
        <WarningBadge key={`${w.code ?? w.message}_${i}`} warning={w} skuName={skuName} week={week} compact />
      ))}
      {hiddenCount > 0 && (
        <Box
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); setExpanded((v) => !v); } }}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.4,
            cursor: "pointer",
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            backgroundColor: "#F1F5F9",
            border: "1px solid #CBD5E1",
            "&:hover": { backgroundColor: "#E2E8F0" },
            width: "fit-content",
          }}
        >
          <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 700, color: "#64748B" }}>
            {expanded ? "Show less" : `+${hiddenCount} more`}
          </Typography>
          <ExpandMoreIcon sx={{ fontSize: 12, color: "#64748B", transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
        </Box>
      )}
    </Stack>
  );
}

// ─── Optimizer error panel ────────────────────────────────────────────────────

function OptimizerErrorPanel({ error }) {
  if (!error) return null;

  const { message } = error;

  return (
    <Alert
      severity="error"
      icon={false}
      sx={{
        mt: 2,
        borderRadius: 2,
        border: "1px solid #FCA5A5",
        backgroundColor: "#FEF2F2",
        alignItems: "flex-start",
        "& .MuiAlert-message": { width: "100%", p: 0 },
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <HighlightOffIcon sx={{ fontSize: 20, color: "#B91C1C", mt: 0.1, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: 13, color: "#7F1D1D", mb: 0.4 }}>
            Optimization failed
          </Typography>
          <Typography variant="body2" sx={{ fontSize: 13, color: "#991B1B", lineHeight: 1.5 }}>
            {message}
          </Typography>
        </Box>
      </Stack>
    </Alert>
  );
}

// ─── Warning group sections ───────────────────────────────────────────────────

function WarningGroupSection({ title, warnings, icon: Icon, accentColor }) {
  const [collapsed, setCollapsed] = useState(false);
  if (warnings.length === 0) return null;
  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 0.75, cursor: "pointer", userSelect: "none" }}
        onClick={() => setCollapsed((v) => !v)}
      >
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Icon sx={{ fontSize: 14, color: accentColor }} />
          <Typography variant="caption" fontWeight={700} sx={{ fontSize: 11, color: accentColor, textTransform: "uppercase", letterSpacing: 0.05 }}>
            {title}
          </Typography>
          <Chip
            label={warnings.length}
            size="small"
            sx={{ height: 16, fontSize: 10, fontWeight: 700, backgroundColor: `${accentColor}18`, color: accentColor, border: `1px solid ${accentColor}30`, "& .MuiChip-label": { px: 0.6 } }}
          />
        </Stack>
        <ExpandMoreIcon sx={{ fontSize: 16, color: accentColor, transform: collapsed ? "rotate(0deg)" : "rotate(180deg)", transition: "transform 0.2s" }} />
      </Stack>
      <Collapse in={!collapsed}>
        <Stack spacing={0.6} sx={{ pl: 0.5 }}>
          {warnings.map((w, i) => (
            <WarningBadge key={`${w.code ?? w.message}_${i}`} warning={w} />
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

function OptimizerWarningsSection({ warnings }) {
  const hasWarnings = warnings.length > 0;
  const { target, businessRisk, dataQuality } = groupWarnings(warnings);

  // Determine highest severity for left accent colour
  const highestSev = hasWarnings
    ? warnings.reduce((best, w) => (severityOrder(w.severity) < severityOrder(best) ? w.severity : best), "info")
    : "info";
  const accentCfg = getWarningSeverityConfig(highestSev);
  const leftAccent = hasWarnings ? accentCfg.color : "#CBD5E1";

  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography
        variant="caption"
        fontWeight={700}
        color="text.secondary"
        display="block"
        mb={1.5}
        sx={{ letterSpacing: 0.06, textTransform: "uppercase" }}
      >
        Optimizer warnings
      </Typography>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2.5,
          border: "1px solid #E8ECF0",
          borderLeft: `4px solid ${leftAccent}`,
          overflow: "hidden",
          backgroundColor: "#fff",
          transition: "box-shadow 0.2s",
          "&:hover": { boxShadow: "0 4px 20px rgba(15,23,42,0.06)" },
        }}
      >
        {/* Header */}
        <Box px={2.25} py={1.75} sx={{ borderBottom: "1px solid #E8ECF0", backgroundColor: "#fff" }}>
          <Stack direction="row" spacing={1.25} alignItems="flex-start">
            <Box
              sx={{
                width: 40, height: 40, borderRadius: 1.5, display: "flex",
                alignItems: "center", justifyContent: "center", flexShrink: 0,
                backgroundColor: hasWarnings ? `${accentCfg.color}12` : "#F4F6F9",
                color: hasWarnings ? accentCfg.color : "text.secondary",
              }}
            >
              {hasWarnings ? <WarningAmberIcon sx={{ fontSize: 22 }} /> : <CheckCircleOutlineIcon sx={{ fontSize: 22 }} />}
            </Box>
            <Box sx={{ minWidth: 0, pt: 0.15 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={{ fontSize: 13 }}>
                  {hasWarnings ? "Optimizer warnings" : "No warnings"}
                </Typography>
                {hasWarnings && (
                  <Chip
                    label={`${warnings.length} warning${warnings.length !== 1 ? "s" : ""}`}
                    size="small"
                    sx={{ height: 20, fontSize: 11, fontWeight: 700, backgroundColor: `${accentCfg.color}15`, color: accentCfg.color, border: `1px solid ${accentCfg.color}30`, "& .MuiChip-label": { px: 0.85 } }}
                  />
                )}
              </Stack>
              {!hasWarnings && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: 13 }}>
                  This optimization run did not return any warnings.
                </Typography>
              )}
            </Box>
          </Stack>
        </Box>

        {/* Grouped warning sections */}
        {hasWarnings && (
          <Box px={2.25} py={1.75} sx={{ backgroundColor: "#fff" }}>
            <Stack spacing={2}>
              <WarningGroupSection
                title="Target warnings"
                warnings={target}
                icon={HighlightOffIcon}
                accentColor={getWarningSeverityConfig("high").color}
              />
              <WarningGroupSection
                title="Business risk"
                warnings={businessRisk}
                icon={WarningAmberIcon}
                accentColor={getWarningSeverityConfig("medium").color}
              />
              <WarningGroupSection
                title="Data quality notes"
                warnings={dataQuality}
                icon={InfoOutlinedIcon}
                accentColor={getWarningSeverityConfig("info").color}
              />
              {/* Fallback: show uncategorised warnings if all groups are empty (shouldn't happen) */}
              {target.length === 0 && businessRisk.length === 0 && dataQuality.length === 0 && (
                <Stack spacing={0.6}>
                  {warnings.map((w, i) => (
                    <WarningBadge key={`${w.code ?? w.message}_${i}`} warning={w} />
                  ))}
                </Stack>
              )}
            </Stack>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

function sortOptimizedCalendarRows(calendar) {
  const list = Array.isArray(calendar) ? [...calendar] : [];
  return list.sort((a, b) => {
    const wa = Number(a.week);
    const wb = Number(b.week);
    if (Number.isFinite(wa) && Number.isFinite(wb) && wa !== wb) return wa - wb;
    return String(a.sku_name ?? "").localeCompare(String(b.sku_name ?? ""));
  });
}

function contributionMarginFromRow(row) {
  const m = row?.metrics ?? {};
  const raw =
    m.expected_contribution_margin ??
    m.contribution_margin ??
    m.expected_profit;
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function isTotalOptimizedRow(row) {
  if (!row || typeof row !== "object") return false;
  if (row.row_type === "SKU_TOTAL") return true;
  if (row.is_total === true || row.is_aggregate === true) return true;
  if (row.row_type === "total") return true;
  const scope = typeof row.week_scope === "string" ? row.week_scope.trim().toLowerCase() : "";
  if (scope === "total") return true;
  const w = row.week;
  if (typeof w === "string") {
    const t = w.trim().toLowerCase();
    if (t === "total" || t === "all") return true;
  }
  return false;
}

function explicitSkuCalendarTotal(planResults, skuKey) {
  if (!planResults || skuKey === undefined || skuKey === null) return null;
  const t = planResults.optimized_calendar_totals;
  if (Array.isArray(t)) {
    return (
      t.find(
        (x) =>
          String(x?.sku_name ?? x?.sku ?? "").trim() === String(skuKey || "").trim()
      ) ?? null
    );
  }
  if (t && typeof t === "object" && !Array.isArray(t)) {
    return t[String(skuKey)] ?? t[skuKey] ?? null;
  }
  return null;
}

/** When the API omits an explicit total row, sum week-level metrics into one synthetic Total row */
function aggregatedTotalRowFromWeeks(skuName, weekRows) {
  if (!weekRows?.length) return null;
  let volume = 0;
  let cmSum = 0;
  let roiWeighted = 0;
  let roiVol = 0;
  const warningsSet = [];
  for (const row of weekRows) {
    const m = row.metrics ?? {};
    const v = Number(m.expected_volume_l);
    if (Number.isFinite(v)) volume += v;
    const cm = contributionMarginFromRow(row);
    if (cm != null) cmSum += cm;
    const roi = Number(m.roi);
    if (Number.isFinite(v) && Number.isFinite(roi)) {
      roiWeighted += v * roi;
      roiVol += v;
    }
    const wl = Array.isArray(m.warnings) ? m.warnings : [];
    for (const w of wl) {
      const key = typeof w === "string" ? w : String(w ?? "");
      if (key.trim()) warningsSet.push(key.trim());
    }
  }
  const uniqWarnings = [...new Set(warningsSet)];
  const roiAvg = roiVol > 0 ? roiWeighted / roiVol : null;
  return {
    sku_name: skuName,
    week: "Total",
    recommended_band: null,
    metrics: {
      expected_volume_l: volume,
      expected_contribution_margin: cmSum,
      roi: roiAvg != null ? roiAvg : undefined,
      warnings: uniqWarnings.length ? uniqWarnings : [],
    },
    _computedTotal: true,
  };
}

function mergeCalendarTotalPreferred({ skuKey, skuRows, planResults }) {
  const fromApiRow = skuRows.find(isTotalOptimizedRow);
  if (fromApiRow) return { totalRow: fromApiRow, weekRows: skuRows.filter((r) => !isTotalOptimizedRow(r)) };
  const weekRowsOnly = skuRows.filter((r) => !isTotalOptimizedRow(r));
  const explicit = explicitSkuCalendarTotal(planResults, skuKey);
  if (explicit && typeof explicit === "object") {
    const nested = typeof explicit.metrics === "object" && explicit.metrics !== null ? { ...explicit.metrics } : {};
    const flat = {
      expected_volume_l: explicit.expected_volume_l,
      roi: explicit.roi,
      expected_contribution_margin:
        explicit.expected_contribution_margin ?? explicit.actual_profit_total ?? explicit.contribution_margin,
      warnings: Array.isArray(explicit.warnings) ? explicit.warnings : undefined,
    };
    const metrics = { ...nested };
    for (const [k, v] of Object.entries(flat)) {
      if (v !== undefined && v !== null && metrics[k] == null) metrics[k] = v;
    }
    const totalRow = {
      sku_name: explicit.sku_name ?? skuKey,
      week: "Total",
      recommended_band:
        explicit.recommended_band ??
        explicit.band ??
        explicit.selected_band ??
        nested.recommended_band,
      metrics,
      is_total: true,
    };
    return { totalRow, weekRows: weekRowsOnly };
  }
  if (weekRowsOnly.length >= 2) {
    return { totalRow: aggregatedTotalRowFromWeeks(skuKey, weekRowsOnly), weekRows: weekRowsOnly };
  }
  if (weekRowsOnly.length === 1) {
    const r = weekRowsOnly[0];
    const totalRow = {
      sku_name: r.sku_name ?? skuKey,
      week: "Total",
      recommended_band: r.recommended_band ?? r.selected_band,
      metrics:
        typeof r.metrics === "object"
          ? { ...r.metrics }
          : {},
      suggested_from_first_week: true,
    };
    return { totalRow, weekRows: weekRowsOnly };
  }
  return { totalRow: null, weekRows: weekRowsOnly };
}

function groupOptimizedCalendarBySku(rows) {
  const map = new Map();
  for (const row of rows) {
    const key = row.sku_name != null ? String(row.sku_name) : "";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(row);
  }
  for (const arr of map.values()) {
    arr.sort((a, b) => {
      const wa = Number(a.week);
      const wb = Number(b.week);
      if (Number.isFinite(wa) && Number.isFinite(wb)) return wa - wb;
      return 0;
    });
  }
  return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
}

function formatWeeksLineUnderSku(skuRows) {
  const weeks = [
    ...new Set(
      skuRows.map((r) => Number(r.week)).filter((n) => Number.isFinite(n))
    ),
  ].sort((a, b) => a - b);
  if (weeks.length === 0) return "";
  if (weeks.length === 1) return `Week ${weeks[0]}`;
  const min = weeks[0];
  const max = weeks[weeks.length - 1];
  const consecutive = weeks.length === max - min + 1;
  if (consecutive) return `Weeks ${min}–${max}`;
  return `Weeks ${weeks.join(", ")}`;
}

function bandChipSx(band) {
  const isNo =
    band === "—" ||
    band == null ||
    band === "NO_PROMO" ||
    String(band).toUpperCase().includes("NO");
  return {
    height: 22,
    fontSize: 11,
    fontWeight: 600,
    maxWidth: "100%",
    backgroundColor: isNo ? "#F2F4F7" : "#EEF1F5",
    color: isNo ? "#757575" : theme.palette.text.primary,
  };
}

/** Fixed column widths so SKU gets most space; expand column stays narrow */
const OPTIMIZER_CALENDAR_COL_WIDTH = {
  SKU: "26%",
  Week: "6%",
  "Promo type": "7%",
  "Recommended band": "8%",
  "Expected volume (L)": "9%",
  "Contribution margin": "10%",
  ROI: "6%",
  Warnings: "16%",
  "": 36,
};

function optimizerCalendarHeaderWidth(header) {
  const w = OPTIMIZER_CALENDAR_COL_WIDTH[header];
  if (w == null) return undefined;
  return w;
}

function OptimizedCalendarTable({ calendar, currencyCode, planResults }) {
  const [expandedSku, setExpandedSku] = useState({});
  const rows = sortOptimizedCalendarRows(calendar);
  const groups = groupOptimizedCalendarBySku(rows);
  const weekDetailRowCount = rows.filter((r) => !isTotalOptimizedRow(r)).length;
  const volFmt = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 });
  const moneyFmt = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currencyCode,
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  });

  const mainHeadColumns = [
    "SKU",
    "Week",
    "Promo type",
    "Recommended band",
    "Expected volume (L)",
    "Contribution margin",
    "ROI",
    "Warnings",
    "",
  ];

  const weekDetailHeadColumns = [
    "Week",
    "Promo type",
    "Recommended band",
    "Expected volume (L)",
    "Contribution margin",
    "ROI",
    "Warnings",
  ];

  const renderRowMetricCells = (
    row,
    weekLabel,
    { stripeIndex = 0, isTotal = false, cellBg = null, borderCol = "#E8ECF0" } = {}
  ) => {
    const m = row?.metrics ?? {};
    const vol = m.expected_volume_l;
    const volDisplay =
      vol != null && Number.isFinite(Number(vol)) ? volFmt.format(Number(vol)) : "—";
    const cm = contributionMarginFromRow(row);
    const cmDisplay = cm != null ? moneyFmt.format(cm) : "—";
    const roiRaw = m.roi;
    const roiDisplay =
      roiRaw != null && Number.isFinite(Number(roiRaw))
        ? `${Number(roiRaw).toFixed(2)}x`
        : "—";
    const band = row.recommended_band ?? row.selected_band ?? "—";
    const promoTypeLabel = formatPromoTypeDisplay(row.promo_type);

    // If an explicit cell background is passed (e.g. for the "By week" sub-table),
    // use it; otherwise fall back to the standard white/stripe pattern.
    const bg =
      cellBg != null
        ? cellBg
        : isTotal
          ? "#fff"
          : stripeIndex % 2 === 1
            ? "#FAFBFC"
            : "#fff";

    return (
      <>
        <TableCell
          sx={{
            fontSize: 12,
            whiteSpace: "nowrap",
            fontWeight: isTotal ? 700 : 500,
            backgroundColor: bg,
            borderColor: borderCol,
            py: 1.15,
          }}
        >
          {weekLabel}
        </TableCell>
        <TableCell
          sx={{
            fontSize: 12,
            fontWeight: isTotal ? 700 : 600,
            backgroundColor: bg,
            borderColor: borderCol,
            py: 1.15,
            px: 1,
            whiteSpace: "nowrap",
          }}
        >
          {promoTypeLabel === "—" ? (
            <Typography variant="caption" color="text.disabled">
              —
            </Typography>
          ) : (
            promoTypeLabel
          )}
        </TableCell>
        <TableCell sx={{ fontSize: 12, backgroundColor: bg, borderColor: borderCol, py: 1.15, px: 1 }}>
          {band === "—" || band == null ? (
            <Typography variant="caption" color="text.disabled">
              —
            </Typography>
          ) : (
            <Chip label={band} size="small" sx={bandChipSx(band)} />
          )}
        </TableCell>
        <TableCell sx={{ fontSize: 12, fontWeight: isTotal ? 700 : 600, backgroundColor: bg, borderColor: borderCol, py: 1.15, px: 1 }}>
          {volDisplay}
        </TableCell>
        <TableCell sx={{ fontSize: 12, backgroundColor: bg, borderColor: borderCol, py: 1.15, px: 1 }}>{cmDisplay}</TableCell>
        <TableCell sx={{ fontSize: 12, fontWeight: isTotal ? 600 : 400, backgroundColor: bg, borderColor: borderCol, py: 1.15, px: 1 }}>
          {roiDisplay}
        </TableCell>
        <TableCell
          sx={{
            verticalAlign: "top",
            backgroundColor: bg,
            borderColor: borderCol,
            py: 1.25,
            px: 1.25,
            minWidth: 168,
          }}
        >
          <RowWarningsCell row={row} />
        </TableCell>
      </>
    );
  };

  const sectionShellSx = {
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid #E8ECF0",
    borderLeft: "4px solid #CBD5E1",
    boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
    backgroundColor: "#fff",
  };

  const calendarHeader = (
    <Box
      sx={{
        px: 2,
        py: 2,
        backgroundColor: "#fff",
        borderBottom: "1px solid #E8ECF0",
      }}
    >
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} justifyContent="space-between">
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#F4F6F9",
              color: "text.secondary",
              flexShrink: 0,
            }}
          >
            <CalendarMonthOutlinedIcon sx={{ fontSize: 26 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={800} color="text.primary" sx={{ letterSpacing: -0.02 }}>
              Optimized calendar
            </Typography>
          </Box>
        </Stack>
        {rows.length > 0 && (
          <Chip
            label={`${groups.length} SKU${groups.length === 1 ? "" : "s"} · ${weekDetailRowCount} week row${weekDetailRowCount === 1 ? "" : "s"}`}
            sx={{
              fontWeight: 700,
              fontSize: 12,
              height: 28,
              alignSelf: { xs: "flex-start", sm: "center" },
              backgroundColor: "#F4F6F9",
              color: "text.secondary",
              border: "1px solid #E8ECF0",
            }}
          />
        )}
      </Stack>
    </Box>
  );

  return (
    <Box sx={{ mb: 2 }}>
      {rows.length === 0 ? (
        <Paper elevation={0} sx={{ ...sectionShellSx }}>
          {calendarHeader}
          <Box sx={{ p: 2.5, backgroundColor: "#fff" }}>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              No optimized calendar rows were returned for this run.
            </Typography>
          </Box>
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ ...sectionShellSx }}>
          {calendarHeader}
          <TableContainer sx={{ backgroundColor: "#fff", overflowX: "auto" }}>
            <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#fff",
                    "& th": { borderBottom: "2px solid #E8ECF0" },
                  }}
                >
                  {mainHeadColumns.map((h) => (
                    <TableCell
                      key={h || "_expand"}
                      component="th"
                      scope="col"
                      sx={{
                        fontWeight: 800,
                        color: "text.secondary",
                        fontSize: 11,
                        py: 1.25,
                        textTransform: "uppercase",
                        letterSpacing: 0.04,
                        width: optimizerCalendarHeaderWidth(h),
                        maxWidth: h === "" ? 36 : undefined,
                        px: h === "" ? 0.5 : 1.5,
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map(([sku, skuRows], groupIdx) => {
                  const skuKey = sku || "_empty_sku";
                  const { totalRow, weekRows } = mergeCalendarTotalPreferred({
                    skuKey: sku,
                    skuRows,
                    planResults,
                  });
                  const open = Boolean(expandedSku[skuKey]);
                  const hasWeeks = weekRows.length > 0;
                  const weeksLine = formatWeeksLineUnderSku(weekRows);
                  const totalSurface = "#fff";

                  return (
                    <React.Fragment key={skuKey}>
                      <TableRow
                        hover={hasWeeks}
                        sx={{
                          "& td": { verticalAlign: "middle", borderColor: "#E8ECF0" },
                          backgroundColor: totalSurface,
                          borderBottom:
                            groups.length > 1 && groupIdx < groups.length - 1
                              ? "2px solid #E8ECF0"
                              : undefined,
                        }}
                      >
                        <TableCell
                          sx={{
                            verticalAlign: "top",
                            backgroundColor: totalSurface,
                            py: 1.25,
                            borderColor: "#E8ECF0",
                            width: OPTIMIZER_CALENDAR_COL_WIDTH.SKU,
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="flex-start">
                            <Inventory2OutlinedIcon
                              sx={{
                                fontSize: 20,
                                color: "text.secondary",
                                mt: 0.15,
                                flexShrink: 0,
                                opacity: 0.85,
                              }}
                            />
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography
                                variant="body2"
                                fontWeight={800}
                                title={sku}
                                sx={{
                                  fontSize: 13,
                                  lineHeight: 1.35,
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                }}
                              >
                                {sku || "—"}
                              </Typography>
                              {weeksLine ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  sx={{ mt: 0.25 }}
                                >
                                  {weeksLine}
                                </Typography>
                              ) : null}
                            </Box>
                          </Stack>
                        </TableCell>
                        {totalRow ? (
                          renderRowMetricCells(totalRow, "Total", { stripeIndex: 0, isTotal: true })
                        ) : (
                          <>
                            <TableCell colSpan={7} sx={{ backgroundColor: totalSurface }}>
                              <Typography variant="caption" color="text.disabled">
                                No calendar metrics for this SKU.
                              </Typography>
                            </TableCell>
                          </>
                        )}
                        <TableCell
                          align="center"
                          sx={{
                            width: OPTIMIZER_CALENDAR_COL_WIDTH[""],
                            maxWidth: 36,
                            px: 0.25,
                            backgroundColor: totalSurface,
                            borderColor: "#E8ECF0",
                            verticalAlign: "middle",
                          }}
                        >
                          <IconButton
                            size="small"
                            disabled={!hasWeeks}
                            aria-expanded={open}
                            aria-label={hasWeeks ? `${open ? "Hide" : "Show"} weekly breakdown for ${sku || "SKU"}` : "No weeks to expand"}
                            onClick={() =>
                              setExpandedSku((prev) =>
                                hasWeeks ? { ...prev, [skuKey]: !prev[skuKey] } : prev
                              )
                            }
                            sx={{
                              color: "text.secondary",
                            }}
                          >
                            <ExpandMoreIcon
                              sx={{
                                fontSize: 28,
                                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                                transition: "transform 0.2s",
                              }}
                            />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={9} sx={{ p: 0, border: "none", backgroundColor: "#F3F6FB" }}>
                          <Collapse in={open && hasWeeks} timeout="auto" unmountOnExit>
                            <Box sx={{ px: 2, py: 1.5, backgroundColor: "#F3F6FB" }}>
                              <Typography
                                variant="caption"
                                fontWeight={700}
                                display="block"
                                mb={1}
                                sx={{
                                  letterSpacing: 0.05,
                                  textTransform: "uppercase",
                                  color: "#4A6FA5",
                                }}
                              >
                                By week
                              </Typography>
                              <TableContainer
                                sx={{
                                  borderRadius: 1,
                                  border: "1px solid #D5E0F0",
                                  backgroundColor: "#EBF1FA",
                                  overflowX: "auto",
                                }}
                              >
                                <Table size="small" sx={{ tableLayout: "fixed" }}>
                                  <TableHead>
                                    <TableRow
                                      sx={{
                                        backgroundColor: "#DDE8F5",
                                        "& th": { borderBottom: "1px solid #C8D8EE" },
                                      }}
                                    >
                                      {weekDetailHeadColumns.map((h) => (
                                        <TableCell
                                          key={`sub_${h}`}
                                          component="th"
                                          scope="col"
                                          sx={{
                                            fontWeight: 800,
                                            color: "#4A6FA5",
                                            fontSize: 10,
                                            py: 0.9,
                                            textTransform: "uppercase",
                                            letterSpacing: 0.04,
                                            ...(h === "Warnings" ? { width: "28%", minWidth: 168 } : {}),
                                          }}
                                        >
                                          {h}
                                        </TableCell>
                                      ))}
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {weekRows.map((row, idx) => (
                                      <TableRow
                                        hover
                                        key={`${skuKey}-w-${row.week}-${idx}`}
                                        sx={{
                                          "& td": { verticalAlign: "top", borderColor: "#D5E0F0" },
                                          "&:hover td": { backgroundColor: "#DDE8F5 !important" },
                                        }}
                                      >
                                        {renderRowMetricCells(row, row.week != null ? `Week ${row.week}` : "—", {
                                          stripeIndex: idx,
                                          isTotal: false,
                                          cellBg: idx % 2 === 0 ? "#EBF1FA" : "#F3F6FB",
                                          borderCol: "#D5E0F0",
                                        })}
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}

function formatContributionTargetPct(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${Number.isInteger(n) ? n : n.toFixed(1)}%`;
}

function TargetAchievementSummaryCards({ targets, results }) {
  const summary = results.plan_summary ?? {};
  const resultObj = results.result ?? {};

  const planVolumeL =
    summary.actual_volume_total ?? resultObj.plan_volume_l ?? null;
  const planCM =
    summary.total_contribution_margin ??
    resultObj.total_contribution_margin ??
    results.total_contribution_margin ??
    null;

  const volumeAchieved =
    summary._volume_target_achieved ??
    resultObj.volume_target_achieved ??
    (planVolumeL != null ? planVolumeL >= targets.volumeL : false);

  const cmAchieved =
    summary._cm_target_achieved ??
    resultObj.cm_target_achieved ??
    (planCM != null ? planCM >= targets.cm : null);

  const volFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });

  const volDiff = planVolumeL != null ? planVolumeL - targets.volumeL : null;
  const cmDiff = planCM != null ? planCM - targets.cm : null;

  const cards = [
    {
      title: "Volume Target",
      targetDisplay: `${volFormatter.format(targets.volumeL)} L`,
      planDisplay: planVolumeL != null ? `${volFormatter.format(planVolumeL)} L` : "—",
      diffDisplay:
        volDiff != null
          ? `${volDiff >= 0 ? "+" : ""}${volFormatter.format(volDiff)} L`
          : "—",
      diffPositive: volDiff != null ? volDiff >= 0 : null,
      achieved: planVolumeL != null ? volumeAchieved : null,
      accent: planVolumeL != null ? (volumeAchieved ? PROMO_COLORS.safe : PROMO_COLORS.warning) : "#999",
    },
    {
      title: "Contribution Margin Target",
      targetDisplay: formatContributionTargetPct(targets.cm),
      planDisplay: planCM != null ? formatContributionTargetPct(planCM) : "—",
      diffDisplay:
        cmDiff != null
          ? `${cmDiff >= 0 ? "+" : ""}${formatContributionTargetPct(cmDiff)}`
          : "—",
      diffPositive: cmDiff != null ? cmDiff >= 0 : null,
      achieved: planCM != null ? cmAchieved : null,
      accent:
        planCM != null
          ? (cmAchieved ? PROMO_COLORS.safe : PROMO_COLORS.warning)
          : "#999",
    },
  ];

  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography
        variant="caption"
        fontWeight={700}
        color="text.secondary"
        display="block"
        mb={1.5}
        sx={{ letterSpacing: 0.06, textTransform: "uppercase" }}
      >
        Target achievement summary
      </Typography>
      <Grid container spacing={2}>
        {cards.map((card) => {
          const ok = card.achieved === true;
          const bad = card.achieved === false;
          const accent = card.accent;
          const borderColor = ok
            ? PROMO_COLORS.safe
            : bad
              ? PROMO_COLORS.warning
              : "#CBD5E1";

          return (
            <Grid item xs={12} md={6} key={card.title}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2.5,
                  border: "1px solid #E8ECF0",
                  borderLeft: `4px solid ${borderColor}`,
                  overflow: "hidden",
                  height: "100%",
                  backgroundColor: "#fff",
                  transition: "box-shadow 0.2s",
                  "&:hover": {
                    boxShadow: "0 4px 20px rgba(15,23,42,0.08)",
                  },
                }}
              >
                {/* Header */}
                <Box
                  px={2.25}
                  pt={2}
                  pb={1.5}
                  sx={{ borderBottom: "1px solid #E8ECF0", backgroundColor: "#fff" }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: accent,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{ color: "text.primary", fontSize: 13 }}
                      >
                        {card.title}
                      </Typography>
                    </Stack>
                    <Chip
                      size="small"
                      icon={
                        ok ? (
                          <CheckCircleOutlineIcon sx={{ fontSize: "13px !important" }} />
                        ) : bad ? (
                          <HighlightOffIcon sx={{ fontSize: "13px !important" }} />
                        ) : undefined
                      }
                      label={card.achieved === null ? "—" : ok ? "Achieved" : "Not achieved"}
                      sx={{
                        height: 22,
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 0.01,
                        backgroundColor: `${accent}18`,
                        color: accent,
                        border: `1px solid ${accent}30`,
                        "& .MuiChip-icon": { color: accent },
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  </Stack>

                  {/* Plan value — hero number */}
                  <Box mt={1.5}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: 11, letterSpacing: 0.02 }}
                    >
                      Plan value
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 26,
                        fontWeight: 800,
                        lineHeight: 1.15,
                        color: accent,
                        letterSpacing: "-0.5px",
                        mt: 0.25,
                      }}
                    >
                      {card.planDisplay}
                    </Typography>
                  </Box>
                </Box>

                {/* Body rows */}
                <Box px={2.25} py={1.5} sx={{ backgroundColor: "#fff" }}>
                  {/* Target value */}
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ py: 0.65 }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
                      Target value
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ fontSize: 13 }}>
                      {card.targetDisplay}
                    </Typography>
                  </Stack>

                  <Box sx={{ borderTop: "1px dashed #E8ECF0" }} />

                  {/* Difference */}
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ py: 0.65 }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
                      Difference (vs target)
                    </Typography>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        backgroundColor: `${accent}15`,
                        border: `1px solid ${accent}30`,
                        borderRadius: 1,
                        px: 0.9,
                        py: 0.2,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: accent,
                          lineHeight: 1.5,
                        }}
                      >
                        {card.diffDisplay}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

export default function OptimizerResults() {
  const dispatch = useDispatch();
  const optimizerResults = useSelector(
    (state) => state.promoSimulator.optimizerResults
  );
  const projectId = useSelector((state) => state.userProject.projectId);
  const selectedBrand = useSelector((state) => state.promoSimulator.selectedBrand);
  const selectedManufacturer = useSelector(
    (state) => state.promoSimulator.selectedManufacturer
  );
  const selectedClass = useSelector((state) => state.promoSimulator.selectedClass);
  const batchOptimizerQueue = useSelector(
    (state) => state.promoSimulator.batchOptimizerQueue || []
  );
  const batchActive = batchOptimizerQueue.length > 0;

  const batchSkuNames = useMemo(
    () => batchOptimizerQueue.map((item) => item.sku).filter(Boolean),
    [batchOptimizerQueue]
  );

  const catalogBrand = selectedBrand || PROMO_ALL_BRANDS_ID;

  const {
    manufacturers,
    apiBrands,
    apiClasses,
    manufacturersLoading,
    brandsLoading,
    classesLoading,
  } = usePromoCatalog(selectedManufacturer, catalogBrand, selectedClass);

  useEffect(() => {
    if (manufacturersLoading || manufacturers.length === 0) return;
    if (!selectedManufacturer || !manufacturers.includes(selectedManufacturer)) {
      dispatch(setSelectedManufacturer(manufacturers[0]));
    }
  }, [manufacturersLoading, manufacturers, selectedManufacturer, dispatch]);

  const brandOptions = useMemo(
    () => (brandsLoading ? [] : [PROMO_ALL_BRANDS_OPTION, ...apiBrands]),
    [apiBrands, brandsLoading]
  );
  const optimizerWeekStart = useSelector((state) => state.promoSimulator.optimizerWeekStart);
  const optimizerWeekEnd = useSelector((state) => state.promoSimulator.optimizerWeekEnd);
  const currencyCode = Number(projectId) === 6 ? "SAR" : "EGP";

  const weekOptions = Array.from({ length: 52 }, (_, i) => i + 1);

  const targetVolume = useSelector((state) => state.promoSimulator.optimizerTargetVolume);
  const targetContributionMargin = useSelector(
    (state) => state.promoSimulator.optimizerTargetContributionMargin
  );
  const lastTargets = useSelector((state) => state.promoSimulator.optimizerLastTargets);
  const [optimizing, setOptimizing] = useState(false);
  /** { message: string, code: string|null, diagnostics: object|null } | null */
  const [optError, setOptError] = useState(null);
  const [resultsRenderEpoch, setResultsRenderEpoch] = useState(0);

  const optimizeEpochRef = useRef(0);
  const optimizeInputsRef = useRef({});

  const clearOptimizationTargets = () => {
    optimizeEpochRef.current += 1;
    setOptimizing(false);
    dispatch(setOptimizerTargetVolume(""));
    dispatch(setOptimizerTargetContributionMargin(""));
    dispatch(setOptimizerLastTargets(null));
    dispatch(setOptimizerWeekStart(null));
    dispatch(setOptimizerWeekEnd(null));
    setOptError(null); // reset structured error
    dispatch(setSelectedBrand(PROMO_ALL_BRANDS_ID));
    dispatch(setSelectedManufacturer(manufacturers[0] ?? ""));
    dispatch(setSelectedClass(PROMO_ALL_CLASSES_ID));
    dispatch(setOptimizerResults(null));
  };

  const volumeNum = Number(String(targetVolume).replace(/[^\d.]/g, ""));
  const volumeValid = Number.isFinite(volumeNum) && volumeNum > 0;

  const cmNum = Number(String(targetContributionMargin).replace(/[^\d.]/g, ""));
  const cmValid = Number.isFinite(cmNum) && cmNum >= 0 && cmNum <= 100;
  const weekRangeValid =
    optimizerWeekStart != null &&
    optimizerWeekEnd != null &&
    Number(optimizerWeekEnd) >= Number(optimizerWeekStart);

  const brandValid = Boolean(catalogBrand);
  const manufacturerValid = Boolean(selectedManufacturer);
  const classValid = Boolean(selectedClass);
  const hasBatchSkus = batchSkuNames.length > 0;
  const canOptimize =
    manufacturerValid &&
    brandValid &&
    (hasBatchSkus || classValid) &&
    volumeValid &&
    cmValid &&
    weekRangeValid &&
    !optimizing;

  optimizeInputsRef.current = {
    manufacturer: selectedManufacturer,
    brand: catalogBrand,
    productClass: selectedClass,
    startWeek: optimizerWeekStart,
    endWeek: optimizerWeekEnd,
    volumeL: volumeNum,
    cm: cmNum,
    skuNames: batchSkuNames,
  };

  const optFieldBaseSx = {
    "& .MuiOutlinedInput-root": { borderRadius: 1.5, backgroundColor: "#fff" },
    "& .MuiInputLabel-root": { fontSize: 14 },
  };

  return (
    <Box>
      {/* ── SKU Scope from Performance History ── */}
      <Collapse in={batchActive} timeout={280} unmountOnExit>
        <Paper
          elevation={0}
          sx={{
            mb: 2.5,
            borderRadius: 2,
            border: "1px solid #7C3AED33",
            bgcolor: "#FAFAFF",
            overflow: "hidden",
          }}
        >
          {/* Header row */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              px: 2.25,
              py: 1.5,
              borderBottom: "1px solid #7C3AED1A",
              bgcolor: "#F5F3FF",
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "#7C3AED18",
                  color: "#7C3AED",
                  flexShrink: 0,
                }}
              >
                <AutoFixHighIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="subtitle2" fontWeight={800} sx={{ color: "#7C3AED", letterSpacing: "-0.01em" }}>
                    SKU Scope
                  </Typography>
                  <Chip
                    label={`${batchOptimizerQueue.length} SKU${batchOptimizerQueue.length !== 1 ? "s" : ""}`}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: 11,
                      fontWeight: 700,
                      bgcolor: "#7C3AED18",
                      color: "#7C3AED",
                    }}
                  />
                  <Tooltip
                    arrow
                    placement="top"
                    title={
                      <>
                        Choose your <strong>manufacturer</strong>, <strong>brand</strong>, <strong>class</strong>,{" "}
                        <strong>week range</strong>, and <strong>targets</strong> below,
                        then click <strong>Optimize Plan</strong>. The optimizer runs only for{" "}
                        {batchOptimizerQueue.length === 1 ? (
                          <>
                            <strong>{batchOptimizerQueue[0]?.sku}</strong>.
                          </>
                        ) : (
                          <>
                            these <strong>{batchOptimizerQueue.length}</strong> SKUs.
                          </>
                        )}
                      </>
                    }
                  >
                    <IconButton
                      size="small"
                      aria-label="How to run optimization for selected SKUs"
                      sx={{
                        color: "#7C3AED",
                        p: 0.35,
                        "&:hover": { bgcolor: "#7C3AED14" },
                      }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                  Selected from Performance History
                </Typography>
              </Box>
            </Stack>

            <Tooltip title="Clear all SKUs" arrow>
              <IconButton
                size="small"
                onClick={() => dispatch(clearBatchOptimizerQueue())}
                sx={{
                  color: "#94A3B8",
                  "&:hover": { color: "#7C3AED", bgcolor: "#7C3AED0F" },
                  flexShrink: 0,
                }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Stack>

          {/* Deletable SKU chips */}
          <Box sx={{ px: 2.25, pt: 1.75, pb: 0.5 }}>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {batchOptimizerQueue.map((item) => (
                <Chip
                  key={item.sku}
                  label={item.sku}
                  size="small"
                  onDelete={() => dispatch(removeFromBatchOptimizerQueue(item.sku))}
                  deleteIcon={
                    <CloseIcon sx={{ fontSize: "14px !important", color: "#94A3B8 !important" }} />
                  }
                  sx={{
                    height: 26,
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#1E293B",
                    bgcolor: "#fff",
                    border: "1px solid #7C3AED2A",
                    borderRadius: 1.5,
                    "& .MuiChip-deleteIcon": {
                      ml: 0.25,
                      "&:hover": { color: "#EF4444 !important" },
                    },
                  }}
                />
              ))}
            </Stack>
          </Box>
        </Paper>
      </Collapse>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        flexDirection={{ xs: "column", sm: "row" }}
        gap={2}
        mb={2}
      >
        <Box>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            color={theme.palette.primary.main}
          >
            Optimizer
          </Typography>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: { xs: 1.75, sm: 2 },
          borderRadius: 2,
          border: "1px solid #F2F4F7",
          backgroundColor: "#FAFBFC",
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={700}
          display="block"
          mb={1}
          sx={{ letterSpacing: 0.03, textTransform: "uppercase" }}
        >
          Optimization targets
        </Typography>
        <Stack spacing={1.5} sx={{ width: "100%", minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              width: "100%",
              alignItems: "flex-start",
            }}
          >
            <TextField
            required
            select
            label="Manufacturer"
            size="small"
            value={
              manufacturers.includes(selectedManufacturer)
                ? selectedManufacturer
                : manufacturers[0] ?? PROMO_DEFAULT_MANUFACTURER
            }
            disabled={manufacturersLoading || manufacturers.length === 0}
            onChange={(e) => {
              dispatch(setSelectedManufacturer(e.target.value));
              dispatch(setSelectedBrand(PROMO_ALL_BRANDS_ID));
              dispatch(setSelectedClass(PROMO_ALL_CLASSES_ID));
            }}
            sx={{
              ...optFieldBaseSx,
              flex: "1 1 180px",
              minWidth: { xs: "100%", sm: "min(180px, 100%)" },
              maxWidth: { xs: "100%", sm: 260 },
            }}
          >
            {manufacturers.map((m) => (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            required
            select
            label="Brand"
            size="small"
            value={catalogBrand}
            disabled={brandsLoading}
            InputLabelProps={{ shrink: Boolean(catalogBrand) }}
            onChange={(e) => {
              const v = e.target.value;
              dispatch(setSelectedBrand(v || PROMO_ALL_BRANDS_ID));
              dispatch(setSelectedClass(PROMO_ALL_CLASSES_ID));
            }}
            sx={{
              ...optFieldBaseSx,
              flex: "1 1 160px",
              minWidth: { xs: "100%", sm: "min(160px, 100%)" },
              maxWidth: { xs: "100%", sm: 240 },
            }}
            SelectProps={{ displayEmpty: true }}
          >
            {brandOptions.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.label ?? b.id}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            required
            select
            label="Class"
            size="small"
            value={
              selectedClass && apiClasses.some((c) => c.id === selectedClass)
                ? selectedClass
                : ""
            }
            disabled={!catalogBrand || classesLoading}
            InputLabelProps={{ shrink: Boolean(selectedClass) }}
            onChange={(e) => {
              dispatch(setSelectedClass(e.target.value || null));
            }}
            sx={{
              ...optFieldBaseSx,
              flex: "1 1 160px",
              minWidth: { xs: "100%", sm: "min(160px, 100%)" },
              maxWidth: { xs: "100%", sm: 240 },
            }}
            SelectProps={{ displayEmpty: true }}
          >
            {apiClasses.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.label ?? c.id}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            required
            select
            label="Start week"
            size="small"
            value={optimizerWeekStart ?? ""}
            onChange={(e) => {
              const v = Number(e.target.value);
              dispatch(setOptimizerWeekStart(v));
              if (optimizerWeekEnd != null && Number(optimizerWeekEnd) < v) {
                dispatch(setOptimizerWeekEnd(v));
              }
            }}
            sx={{
              ...optFieldBaseSx,
              flex: "1 1 118px",
              minWidth: { xs: "100%", sm: "min(118px, 45%)" },
              maxWidth: { xs: "100%", sm: 168 },
            }}
          >
            {weekOptions.map((w) => (
              <MenuItem key={`sw_${w}`} value={w}>
                Week {w}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            required
            select
            label="End week"
            size="small"
            value={optimizerWeekEnd ?? ""}
            onChange={(e) => {
              dispatch(setOptimizerWeekEnd(Number(e.target.value)));
            }}
            error={
              optimizerWeekEnd != null &&
              optimizerWeekStart != null &&
              Number(optimizerWeekEnd) < Number(optimizerWeekStart)
            }
            sx={{
              ...optFieldBaseSx,
              flex: "1 1 118px",
              minWidth: { xs: "100%", sm: "min(118px, 45%)" },
              maxWidth: { xs: "100%", sm: 168 },
            }}
          >
            {weekOptions
              .filter((w) => optimizerWeekStart == null || w >= Number(optimizerWeekStart))
              .map((w) => (
                <MenuItem key={`ew_${w}`} value={w}>
                  Week {w}
                </MenuItem>
              ))}
          </TextField>
          <TextField
            required
            label="Target volume"
            size="small"
            value={targetVolume}
            onChange={(e) => {
              const raw = e.target.value;
              const cleaned = raw.replace(/[^\d]/g, "");
              dispatch(setOptimizerTargetVolume(cleaned));
            }}
            placeholder="e.g. 25000"
            sx={{
              ...optFieldBaseSx,
              flex: "1 1 180px",
              minWidth: { xs: "100%", sm: "min(180px, 100%)" },
              maxWidth: { xs: "100%", sm: 260 },
              "& input::placeholder": { fontSize: 12 },
            }}
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
            error={Boolean(targetVolume) && !volumeValid}
            InputProps={{
              endAdornment: <InputAdornment position="end">Liters</InputAdornment>,
            }}
          />
          <TextField
            required
            label="Target contribution"
            size="small"
            value={targetContributionMargin}
            onChange={(e) => {
              const raw = e.target.value;
              let cleaned = raw.replace(/[^\d.]/g, "");
              const dotParts = cleaned.split(".");
              if (dotParts.length > 2) {
                cleaned = `${dotParts[0]}.${dotParts.slice(1).join("")}`;
              }
              dispatch(setOptimizerTargetContributionMargin(cleaned));
            }}
            placeholder="e.g. 25"
            sx={{
              ...optFieldBaseSx,
              flex: "1 1 200px",
              minWidth: { xs: "100%", sm: "min(200px, 100%)" },
              maxWidth: { xs: "100%", sm: "min(340px, 100%)" },
              "& input::placeholder": { fontSize: 12 },
            }}
            inputProps={{ inputMode: "decimal" }}
            error={Boolean(targetContributionMargin) && !cmValid}
            helperText={
              Boolean(targetContributionMargin) && !cmValid
                ? "Must be between 0 and 100%"
                : undefined
            }
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
          />
          </Box>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            justifyContent={{ xs: "stretch", sm: "flex-end" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{
              width: "100%",
              minWidth: 0,
              pt: 0.25,
            }}
          >
            <Button
              size="small"
              variant="contained"
              startIcon={
                optimizing ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <AutoFixHighIcon />
                )
              }
              disabled={!canOptimize}
              onClick={async () => {
                if (!canOptimize) return;
                const {
                  manufacturer,
                  brand,
                  productClass,
                  startWeek,
                  endWeek,
                  volumeL,
                  cm,
                  skuNames,
                } = optimizeInputsRef.current;
                const hasSkus = skuNames?.length > 0;
                if (
                  !manufacturer ||
                  !brand ||
                  (!hasSkus && !productClass) ||
                  startWeek == null ||
                  endWeek == null ||
                  Number(endWeek) < Number(startWeek) ||
                  !Number.isFinite(volumeL) ||
                  volumeL <= 0 ||
                  !Number.isFinite(cm) ||
                  cm < 0 ||
                  cm > 100
                ) {
                  return;
                }
                const epoch = ++optimizeEpochRef.current;
                setOptError(null);
                setOptimizing(true);
                dispatch(setOptimizerLastTargets({ volumeL, cm }));
                try {
                  const res = await promoSimulatorService.getOptimizerResults({
                    projectId,
                    manufacturer,
                    brand,
                    productClass: hasSkus ? undefined : productClass,
                    start_week: startWeek,
                    end_week: endWeek,
                    targetVolumeL: volumeL,
                    targetContributionMargin: cm,
                    sku_names: hasSkus ? skuNames : undefined,
                  });
                  if (epoch !== optimizeEpochRef.current) return;
                  dispatch(setOptimizerResults(res));
                  setResultsRenderEpoch((n) => n + 1);
                } catch (e) {
                  if (epoch !== optimizeEpochRef.current) return;
                  setOptError({
                    message: e?.message || "Failed to optimize plan.",
                    code: e?.apiCode ?? null,
                    diagnostics: e?.apiDiagnostics ?? null,
                  });
                } finally {
                  if (epoch === optimizeEpochRef.current) setOptimizing(false);
                }
              }}
              sx={{
                whiteSpace: "nowrap",
                flexShrink: 0,
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: 132 },
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                fontSize: 13,
                px: 1.75,
                py: 0.9,
              }}
            >
              {optimizing ? "Loading…" : "Optimize Plan"}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={clearOptimizationTargets}
              sx={{
                whiteSpace: "nowrap",
                flexShrink: 0,
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: 132 },
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                fontSize: 13,
                px: 1.75,
                py: 0.9,
                borderColor: "#E0E4EA",
                color: "text.secondary",
              }}
            >
              Clear targets
            </Button>
          </Stack>
        </Stack>
        <OptimizerErrorPanel error={optError} />
      </Paper>

      {optimizing && (
        <Box
          py={8}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={2}
        >
          <CircularProgress size={40} sx={{ color: theme.palette.primary.main }} />
          <Typography color="text.secondary" fontWeight={600}>
            Loading optimizer results…
          </Typography>
        </Box>
      )}

      {!optimizing && !optimizerResults && (
        <Box py={8} textAlign="center">
          <AutoFixHighIcon sx={{ fontSize: 60, color: "#ccc", mb: 1.5 }} />
          <Typography color="text.secondary">
            Click <strong>Optimize Plan</strong> to generate recommendations and comparisons with your plan.
          </Typography>
        </Box>
      )}

      {!optimizing && optimizerResults && (
        <Box key={resultsRenderEpoch}>
          {/* Target vs Plan comparison */}
          {lastTargets && (
            <TargetAchievementSummaryCards
              targets={lastTargets}
              results={optimizerResults}
            />
          )}

          <OptimizerWarningsSection warnings={normalizeOptimizerWarnings(optimizerResults)} />

          <OptimizedCalendarTable
            calendar={optimizerResults?.optimized_calendar ?? optimizerResults?.calendar_cells}
            currencyCode={currencyCode}
            planResults={optimizerResults}
          />
        </Box>
      )}
    </Box>
  );
}

